import React, { useState, useEffect, useCallback, useMemo } from "react";
import Dish from "./Dish";
import { Section } from "react-scroll-section";
import moment from 'moment';

import { 
  Box, 
  Typography, 
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { filterVisibility, decodeRestaurantId } from "util/utils";
import { checkDishIsInActive } from 'util/orderUtils';
import { database, ref, onValue } from "util/firebase";
import { useSelector } from "react-redux";

// Modern styling using sx prop
const styles = {
  root: {
    width: '100%',
    maxWidth: '100%',
    margin: 0,
    padding: { xs: 1, sm: 2 },
    overflowX: 'hidden',
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 3,
    padding: { xs: 2, sm: 3 },
  },
  logo: {
    width: { xs: '80%', sm: '60%', md: '40%' },
    maxWidth: 400,
    height: 'auto',
    borderRadius: 3,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.3s ease',
    '&:hover': {
      transform: 'scale(1.02)',
    }
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 300,
    padding: 4,
    textAlign: 'center',
  },
  errorIcon: {
    width: 80,
    height: 80,
    marginBottom: 2,
    opacity: 0.6,
  },
  errorText: {
    color: 'text.secondary',
    marginTop: 2,
  },
  categoryHeader: {
    background: '#f5f5f5',
    color: 'text.primary',
    padding: { xs: 0.75, sm: 1 },
    marginBottom: 1.5,
    borderRadius: { xs: 0, sm: 1 },
    boxShadow: 'none',
    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
    width: '100%',
    maxWidth: '100%',
  },
  categoryTitle: {
    fontWeight: 600,
    fontSize: { xs: '0.9rem', sm: '1rem' },
    lineHeight: 1.3,
  },
  categorySubtitle: {
    fontWeight: 400,
    fontSize: { xs: '0.75rem', sm: '0.8rem' },
    opacity: 0.7,
    marginTop: 0.25,
    color: 'text.secondary',
  },
  sectionContainer: {
    marginBottom: 4,
    background: '#ffffff',
    borderRadius: { xs: 0, sm: 3 },
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    width: '100%',
    maxWidth: '100%',
  },
  emptyState: {
    textAlign: 'center',
    padding: 4,
    color: 'text.secondary',
  },
};

const CategorySections = (props) => {
  const { categories, restaurantId } = props;
  const [filteredDishes, setFilteredDishes] = useState([]);
  const restaurant = useSelector((state) => state.menu.restaurant);
  const [imageVisible, setImageVisible] = useState(false);
  const [sectionsReady, setSectionsReady] = useState(false);
  const todayString = moment(new Date()).format('YYYY-MM-DD');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Defer Section rendering until after initial mount to avoid setState during render
  useEffect(() => {
    // Use double requestAnimationFrame to ensure we're past the initial render cycle
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setSectionsReady(true);
      });
    });
  }, []);

  useEffect(() => {
    if (!restaurantId) return;
    
    const dbRef = ref(database, `inactiveMenus/${decodeRestaurantId(restaurantId)}`);
    
    const unsubscribe = onValue(dbRef, (snapshot) => {
      if (snapshot && snapshot.val() && snapshot.val().dishIds) {
        const inactiveIds = snapshot.val().dishIds;
        setFilteredDishes(inactiveIds);
      } else {
        setFilteredDishes([]);
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [restaurantId]);

  const itemImage = useCallback((category, itemId) => {
    const allImages = categories.map((category) => category.images);
    const filterImages = allImages.filter(
      (images) => images && itemId in images
    );
    if (filterImages && filterImages.length > 0) {
      return filterImages[0][itemId];
    }

    return null;
  }, [categories]);

  const filteredCategories = useMemo(() => {
    return categories.map((category) => ({
      ...category,
      filteredItems: category.items ? category.items.filter((dish) => {
        return checkDishIsInActive(dish, filteredDishes, todayString) && filterVisibility(dish);
      }) : []
    }));
  }, [categories, filteredDishes, todayString]);

  return (
    <Box sx={styles.root}>
      {imageVisible ? null : (
        <>
          {restaurant && restaurant.logo ? (
            <Box sx={styles.logoContainer}>
              <img 
                style={styles.logo} 
                src={restaurant.logo} 
                alt="Restaurant Logo" 
              />
            </Box>
          ) : null}
          
          {categories.length === 0 ? (
            <Box sx={styles.errorContainer}>
              <img
                style={styles.errorIcon}
                src={`/static/images/refresh.png`}
                alt="refresh"
              />
              <Typography variant="h6" sx={styles.errorText}>
                No menu items available
              </Typography>
              <Typography variant="body2" sx={styles.errorText}>
                Please refresh the page and try again.
              </Typography>
            </Box>
          ) : null}
        </>
      )}

      {filteredCategories &&
        filteredCategories.map((category, index) => {
          const content = (
            <Box sx={styles.sectionContainer}>
              <Box sx={styles.categoryHeader}>
                <Typography sx={styles.categoryTitle}>
                  {category.name}
                </Typography>
                {category.altName && (
                  <Typography sx={styles.categorySubtitle}>
                    {category.altName}
                  </Typography>
                )}
              </Box>
              
              {category.filteredItems.length > 0 ? (
                category.filteredItems.map((item) => {
                  return (
                    <Dish
                      key={item.id}
                      item={item}
                      image={itemImage(
                        categories[props.categoryIndex],
                        item.id
                      )}
                    />
                  );
                })
              ) : (
                <Box sx={styles.emptyState}>
                  <Typography variant="body1">
                    No items available in this category
                  </Typography>
                </Box>
              )}
            </Box>
          );

          // Only render Section components after initial mount to avoid setState during render
          return sectionsReady ? (
            <Section key={category.id} id={category.id}>
              {content}
            </Section>
          ) : (
            <div key={category.id} id={category.id}>
              {content}
            </div>
          );
        })}
    </Box>
  );
};

export default CategorySections;
